import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Archive, ArchiveRestore, Loader2, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { useQueryClient, type QueryKey } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useNetwork } from '@/contexts/NetworkContext'
import { apiErrorDescription } from '@/services/core/apiError'

/** The API's lifecycle state for any object: live, archived or deleted. */
export type ObjectState = 'ACTIVE' | 'INACTIVE' | 'HIDDEN'

/** Translation key per target state: activate, archive or delete. */
const ACTION_KEY: Record<ObjectState, string> = {
  ACTIVE: 'unarchive',
  INACTIVE: 'archive',
  HIDDEN: 'delete',
}

/**
 * A marketplace-only network can archive but not delete. Hybrid networks are
 * ad server customers too, so they keep delete.
 */
export function useCanDeleteObjects() {
  const { currentNetwork } = useNetwork()
  return !(currentNetwork?.marketplacePlatform && !currentNetwork?.hybridMarketplacePlatform)
}

/**
 * Saves an object's state on its own and patches the cached object, so the
 * page's form and its unsaved edits are left alone.
 */
export function useSaveObjectState(
  update: (objectState: ObjectState) => Promise<unknown>,
  queryKey: QueryKey,
  listKey?: QueryKey
) {
  const queryClient = useQueryClient()
  return async (objectState: ObjectState) => {
    await update(objectState)
    queryClient.setQueryData(queryKey, (old: object | undefined) => old && { ...old, objectState })
    if (listKey) queryClient.invalidateQueries({ queryKey: listKey })
  }
}

export interface ObjectStateToggleProps {
  /** The object's saved state. */
  value: string
  /** Saves the new state straight to the API — no save bar involved. */
  onChange: (state: ObjectState) => Promise<unknown>
  /** Offer delete — defaults to whether the network is an ad server customer. */
  allowDelete?: boolean
}

/**
 * An archive toggle, plus a delete button once the object is archived. Every
 * action asks for confirmation, then saves immediately.
 */
export function ObjectStateToggle({ value, onChange, allowDelete }: ObjectStateToggleProps) {
  const { t } = useTranslation()
  const canDelete = useCanDeleteObjects()
  const withDelete = allowDelete ?? canDelete
  const [pending, setPending] = useState<ObjectState | null>(null)
  const [saving, setSaving] = useState(false)
  const isActive = value === 'ACTIVE'

  const actions: ObjectState[] = [isActive ? 'INACTIVE' : 'ACTIVE']
  if (withDelete && value === 'INACTIVE') actions.push('HIDDEN')

  const confirm = async () => {
    if (!pending) return
    const key = ACTION_KEY[pending]
    setSaving(true)
    try {
      await onChange(pending)
      toast.success(t(`objectState.${key}Done`), { duration: 3000 })
      setPending(null)
    } catch (error) {
      toast.error(t('objectState.failed'), { description: apiErrorDescription(error), duration: 5000 })
    } finally {
      setSaving(false)
    }
  }

  const pendingKey = pending ? ACTION_KEY[pending] : null

  return (
    <div className="flex items-center gap-1">
      {actions.map((state) => {
        const label = t(`objectState.${ACTION_KEY[state]}`)
        const Icon = state === 'HIDDEN' ? Trash2 : state === 'ACTIVE' ? ArchiveRestore : Archive
        return (
          <Tooltip key={state}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label={label}
                onClick={() => setPending(state)}
              >
                <Icon />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{label}</TooltipContent>
          </Tooltip>
        )
      })}

      <AlertDialog open={!!pending} onOpenChange={(open) => !open && !saving && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingKey && t(`objectState.${pendingKey}Title`)}</AlertDialogTitle>
            <AlertDialogDescription>{pendingKey && t(`objectState.${pendingKey}Description`)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              variant={pending === 'HIDDEN' ? 'destructive' : 'default'}
              disabled={saving}
              onClick={(e) => {
                // Keep the dialog open until the save settles.
                e.preventDefault()
                confirm()
              }}
            >
              {saving && <Loader2 className="animate-spin" />}
              {t('common.yes')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
